import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Rocket, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SearchableSelect from "@/components/SearchableSelect";
import PunchoutSandboxNotice, { SANDBOX_PRESET, isSandboxSupplier } from "@/components/PunchoutSandboxNotice";

const EMPTY = { name: "", punchout_url: "", from_identity: "", to_identity: "", shared_secret: "", buyer_cookie_prefix: "SS",
  order_request_url: "", deployment_mode: "test", linked_vendor_id: "", active: true };

// Settings > Punchout (Package 20): per-contractor supplier accounts.
// Credentials are write-only — the API only ever returns a masked secret.
export default function PunchoutSettingsPanel() {
  const [suppliers, setSuppliers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [editing, setEditing] = useState(null); // null | {form, id?}
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);

  const load = () => api.get("/contractor/punchout/suppliers").then((r) => setSuppliers(r.data));
  useEffect(() => {
    load();
    api.get("/contractor/vendor-network").then((r) => setVendors(r.data));
  }, []);

  const testConnection = async (s) => {
    setTesting(s.id);
    try {
      const r = await api.post(`/contractor/punchout/suppliers/${s.id}/test-connection`);
      if (r.data.ok) toast.success(`✓ ${s.name}: ${r.data.message}`);
      else toast.error(`${s.name} failed at ${r.data.step}: ${r.data.message}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally {
      setTesting(null);
    }
  };

  const save = async () => {
    const f = editing.form;
    if (!f.name.trim() || !f.punchout_url.trim() || !f.from_identity.trim() || !f.to_identity.trim()) {
      return toast.error("Name, punchout URL, and both identities are required");
    }
    if (!editing.id && !f.shared_secret) return toast.error("A shared secret is required");
    setSaving(true);
    try {
      const payload = { ...f, order_request_url: f.order_request_url || null, linked_vendor_id: f.linked_vendor_id || null };
      if (editing.id) {
        await api.patch(`/contractor/punchout/suppliers/${editing.id}`, payload);
        toast.success("Supplier updated");
      } else {
        await api.post("/contractor/punchout/suppliers", payload);
        toast.success("Supplier added");
      }
      setEditing(null);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.message).join(" ") : d || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    await api.delete(`/contractor/punchout/suppliers/${s.id}`);
    toast.success(`${s.name} removed`);
    load();
  };

  const set = (patch) => setEditing((e) => ({ ...e, form: { ...e.form, ...patch } }));

  return (
    <div data-testid="punchout-settings-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-semibold text-[14px] flex items-center gap-1.5"><Rocket size={14} className="text-sky-500" /> Punchout Suppliers</div>
          <p className="text-[12.5px] text-slate-500 mt-0.5">
            cXML punchout accounts (Home Depot / Lowe's / Grainger…). Certifying a live supplier is just entering
            the credentials they issue — the engine is the same one the dev simulator runs against.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setEditing({ sandbox: true, form: { ...EMPTY, ...SANDBOX_PRESET } })} data-testid="add-punchout-sandbox-button" className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-slate-200 text-[12.5px] font-semibold hover:bg-slate-50">
            <FlaskConical size={13} /> Add PunchOut Sandbox
          </button>
          <button onClick={() => setEditing({ form: { ...EMPTY } })} data-testid="add-punchout-supplier-button" className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[12.5px] font-semibold">
            <Plus size={13} /> Add Supplier
          </button>
        </div>
      </div>

      <div className="mt-4 space-y-2.5">
        {suppliers.map((s) => (
          <div key={s.id} className="border border-slate-200 rounded-xl p-4 flex items-center justify-between flex-wrap gap-2" data-testid={`punchout-supplier-${s.id}`}>
            <div>
              <div className="font-semibold text-[13.5px]">{s.name}</div>
              <div className="text-[11.5px] text-slate-400 mono mt-0.5">{s.punchout_url}</div>
              <div className="text-[11.5px] text-slate-400 mt-0.5">
                {s.from_identity} → {s.to_identity} · secret {s.shared_secret_masked}
              </div>
              <div className="text-[11.5px] mt-0.5 flex items-center gap-2 flex-wrap">
                <span className={`ss-pill ${s.deployment_mode === "production" ? "ss-pill-green" : "ss-pill-amber"}`}>{s.deployment_mode || "test"} mode</span>
                {s.order_request_url
                  ? <span className="text-slate-400">Electronic ordering: <span className="mono">{s.order_request_url}</span></span>
                  : <span className="text-amber-600">No order URL — orders can't transmit electronically yet</span>}
                {s.linked_vendor_id && <span className="text-slate-400">books under {(vendors.find((v) => v.vendor_id === s.linked_vendor_id) || {}).vendor_name || "linked vendor"}</span>}
              </div>
              {isSandboxSupplier(s) && <PunchoutSandboxNotice className="mt-2" />}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => testConnection(s)} disabled={testing === s.id} data-testid={`test-connection-${s.id}`} className="px-3 py-1.5 rounded-lg border border-slate-200 text-[12px] font-semibold hover:bg-slate-50 disabled:opacity-50">
                {testing === s.id ? "Testing…" : "Test connection"}
              </button>
              <StatusBadge status={s.active ? "active" : "disconnected"} />
              <button onClick={() => setEditing({ id: s.id, form: { ...s, shared_secret: "" } })} data-testid={`edit-supplier-${s.id}`} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50"><Pencil size={13} /></button>
              <button onClick={() => remove(s)} data-testid={`delete-supplier-${s.id}`} className="p-2 rounded-lg border border-slate-200 text-slate-400 hover:text-red-500"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
        {suppliers.length === 0 && <div className="text-[13px] text-slate-400 py-6 text-center">No punchout suppliers configured.</div>}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="punchout-supplier-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit" : "Add"} {editing?.sandbox ? "PunchOut Sandbox" : "Punchout Supplier"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              {(editing.sandbox || isSandboxSupplier(editing.form)) && <PunchoutSandboxNotice />}
              {editing.sandbox && (
                <ol className="list-decimal pl-5 space-y-1 text-[12.5px] text-slate-600" data-testid="punchout-sandbox-steps">
                  <li>Create a free account at <a href="https://punchoutsandbox.com/signup" target="_blank" rel="noopener noreferrer" className="text-sky-600 underline">punchoutsandbox.com/signup</a>.</li>
                  <li>In your sandbox account, copy your <b>Identity</b> and paste it into <b>To identity (supplier's)</b> below.</li>
                  <li>Copy your <b>Shared secret</b> and paste it into <b>Shared secret</b> below.</li>
                  <li>Enter your own name under <b>From identity (yours)</b>, e.g. your company name.</li>
                  <li>Save, then press <b>Test connection</b>. Shop it from the Catalog under <b>Shop at Supplier (Punchout)</b>.</li>
                </ol>
              )}
              {[
                ["name", "Supplier name", "Home Depot Pro"],
                ["punchout_url", "PunchOutSetupRequest URL", "https://…/punchout/setup"],
                ["from_identity", "From identity (yours)", "scapesource-buildright"],
                ["to_identity", "To identity (supplier's)", editing.sandbox ? "Your sandbox Identity" : "homedepot"],
                ["buyer_cookie_prefix", "Buyer cookie prefix", "SS"],
              ].map(([key, label, ph]) => (
                <div key={key}>
                  <label className="text-[12px] font-medium text-slate-700">{label}</label>
                  <input value={editing.form[key] || ""} onChange={(e) => set({ [key]: e.target.value })} placeholder={ph} data-testid={`supplier-${key}-input`} className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 text-[13px]" />
                </div>
              ))}
              <div>
                <label className="text-[12px] font-medium text-slate-700">Order request URL <span className="text-slate-400">(electronic PO transmission — from the supplier's B2B team)</span></label>
                <input value={editing.form.order_request_url || ""} onChange={(e) => set({ order_request_url: e.target.value })} placeholder="https://…/orders/cxml" data-testid="supplier-order-url-input" className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 text-[13px]" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[12px] font-medium text-slate-700">Deployment mode</label>
                  <SearchableSelect value={editing.form.deployment_mode || "test"} onChange={(e) => set({ deployment_mode: e.target.value })} data-testid="supplier-mode-select" className="mt-1 w-full h-9 px-2 rounded-lg border border-slate-200 text-[13px]">
                    <option value="test">Test (certification)</option>
                    <option value="production">Production</option>
                  </SearchableSelect>
                </div>
                <div>
                  <label className="text-[12px] font-medium text-slate-700">Books under vendor</label>
                  <SearchableSelect value={editing.form.linked_vendor_id || ""} onChange={(e) => set({ linked_vendor_id: e.target.value })} data-testid="supplier-vendor-select" className="mt-1 w-full h-9 px-2 rounded-lg border border-slate-200 text-[13px]">
                    <option value="">— pick network vendor —</option>
                    {vendors.map((v) => <option key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</option>)}
                  </SearchableSelect>
                </div>
              </div>
              <div>
                <label className="text-[12px] font-medium text-slate-700">Shared secret {editing.id && <span className="text-slate-400">(blank = keep current)</span>}</label>
                <input type="password" value={editing.form.shared_secret || ""} onChange={(e) => set({ shared_secret: e.target.value })} data-testid="supplier-shared-secret-input" className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 text-[13px]" />
              </div>
              <label className="flex items-center gap-2 text-[12.5px] text-slate-600">
                <input type="checkbox" checked={editing.form.active} onChange={(e) => set({ active: e.target.checked })} /> Active
              </label>
              <button disabled={saving} onClick={save} data-testid="save-supplier-button" className="w-full h-10 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold disabled:opacity-60">
                {saving ? "Saving…" : "Save Supplier"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
